import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, Send, Copy, Download, Mail, Plus, X } from "lucide-react";
import DOMPurify from "dompurify";
import api, { getBoardroomToken } from "../utils/qccaApi";

// Minimal markdown → HTML (headings, bold, lists, paragraphs). No external deps.
function mdToHtml(md) {
  if (!md) return "";
  const lines = md.split(/\n/);
  const out = [];
  let inList = false;
  const closeList = () => { if (inList) { out.push("</ul>"); inList = false; } };
  for (let line of lines) {
    const m1 = line.match(/^#\s+(.+)/);
    const m2 = line.match(/^##\s+(.+)/);
    const m3 = line.match(/^-\s+(.+)/);
    if (m1) { closeList(); out.push(`<h1>${escape(m1[1])}</h1>`); }
    else if (m2) { closeList(); out.push(`<h2>${escape(m2[1])}</h2>`); }
    else if (m3) { if (!inList) { out.push("<ul>"); inList = true; } out.push(`<li>${inline(m3[1])}</li>`); }
    else if (line.trim() === "") { closeList(); out.push("<br/>"); }
    else { closeList(); out.push(`<p>${inline(line)}</p>`); }
  }
  closeList();
  return out.join("");
}
function escape(s) { return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); }
function inline(s) {
  return escape(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>");
}

export default function QCCAMinuteBookDoc() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientDraft, setRecipientDraft] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!getBoardroomToken()) { nav("/qcca/boardroom/login"); return; }
    api.get(`/qcca/minute-book/documents/${id}`)
      .then((r) => setDoc(r.data))
      .catch(() => { toast.error("Document not found."); nav("/qcca/boardroom/minutebook"); })
      .finally(() => setLoading(false));
  }, [id, nav]);

  const share = async () => {
    if (recipients.length === 0) { toast.error("Add at least one recipient."); return; }
    setSending(true);
    try {
      const r = await api.post(`/qcca/minute-book/documents/${id}/share`, { recipients, note: note.trim() || null });
      toast.success(`${r.data.queued} message${r.data.queued === 1 ? "" : "s"} queued. (Email delivery is MOCKED in local preview.)`);
      setShareOpen(false); setRecipients([]); setNote("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not share."); }
    finally { setSending(false); }
  };

  const addRecipient = () => {
    const e = recipientDraft.trim().toLowerCase();
    if (!e || !/.+@.+\..+/.test(e)) { toast.error("Enter a valid email."); return; }
    if (recipients.includes(e)) { toast.error("Already added."); return; }
    setRecipients((r) => [...r, e]); setRecipientDraft("");
  };

  if (loading || !doc) return <div className="min-h-screen bg-black text-gray-500 font-mono text-sm p-10">Loading…</div>;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/qcca/boardroom/minutebook" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 hover:text-[#C5A52A] mb-6">
          <ArrowLeft className="w-3 h-3" /> Minute Book
        </Link>

        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Minute Book</div>
            <h1 className="mt-2 font-headline text-4xl text-white leading-tight" data-testid="qcca-mb-doc-title">{doc.title}</h1>
            <div className="mt-2 font-mono text-[11px] text-gray-500">
              {doc.meeting_date || new Date(doc.created_at).toLocaleDateString()} · {doc.corporation}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { navigator.clipboard.writeText(doc.body_markdown); toast.success("Markdown copied."); }}
              className="inline-flex items-center gap-2 border border-[#1a1a1a] hover:border-[#C5A52A] text-gray-300 hover:text-white font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 transition-colors"
              data-testid="qcca-mb-copy">
              <Copy className="w-3 h-3" /> Copy markdown
            </button>
            <a download={`${doc.title.replace(/[^a-z0-9]+/gi, "-")}.md`}
              href={`data:text/markdown;charset=utf-8,${encodeURIComponent(doc.body_markdown)}`}
              className="inline-flex items-center gap-2 border border-[#1a1a1a] hover:border-[#C5A52A] text-gray-300 hover:text-white font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 transition-colors"
              data-testid="qcca-mb-download">
              <Download className="w-3 h-3" /> Download
            </a>
            <button onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 transition-colors"
              data-testid="qcca-mb-share-btn">
              <Mail className="w-3 h-3" /> Email to board
            </button>
          </div>
        </div>

        {/* Document body */}
        <article className="bg-[#0a0a0a] border border-[#1a1a1a] p-8 lg:p-12 prose prose-invert max-w-none font-sans"
          data-testid="qcca-mb-doc-body"
          style={{ lineHeight: 1.7 }}>
          <style>{`
            [data-testid="qcca-mb-doc-body"] h1 { font-family: Fraunces, serif; font-size: 2.25rem; color: #fff; margin: 0 0 0.5em; line-height: 1.1; }
            [data-testid="qcca-mb-doc-body"] h2 { font-family: Fraunces, serif; font-size: 1.35rem; color: #C5A52A; margin: 1.5em 0 0.4em; letter-spacing: 0.01em; }
            [data-testid="qcca-mb-doc-body"] p { color: #d1d5db; margin: 0.6em 0; }
            [data-testid="qcca-mb-doc-body"] ul { list-style: disc; padding-left: 1.3em; color: #d1d5db; margin: 0.6em 0; }
            [data-testid="qcca-mb-doc-body"] li { margin: 0.2em 0; }
            [data-testid="qcca-mb-doc-body"] strong { color: #fff; }
            [data-testid="qcca-mb-doc-body"] em { color: #C5A52A; font-style: italic; }
          `}</style>
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(mdToHtml(doc.body_markdown)) }} />
        </article>
      </div>

      {/* Share modal */}
      {shareOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShareOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0a] border border-[#1a1a1a] p-8 max-w-lg w-full" data-testid="qcca-mb-share-modal">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Share with board</div>
                <h3 className="mt-2 font-headline text-2xl text-white">Email these minutes.</h3>
              </div>
              <button onClick={() => setShareOpen(false)} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">Recipients</label>
                <div className="flex gap-2">
                  <input value={recipientDraft} onChange={(e) => setRecipientDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                    placeholder="email@address.ca"
                    className="flex-1 bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-white placeholder-gray-600 font-mono text-sm px-3 py-2 outline-none transition-colors"
                    data-testid="qcca-mb-share-email-input" />
                  <button onClick={addRecipient}
                    className="bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2"><Plus className="w-3 h-3" /></button>
                </div>
                {recipients.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="qcca-mb-share-recipients">
                    {recipients.map((r, i) => (
                      <span key={`recipient-${r}-${i}`} className="inline-flex items-center gap-1 border border-[#C5A52A]/40 text-[#C5A52A] font-mono text-xs px-2 py-1">
                        {r}
                        <button onClick={() => setRecipients(recipients.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400 mb-2">Optional note</label>
                <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Please review and reply with any corrections by Friday."
                  className="w-full bg-black border border-[#1a1a1a] focus:border-[#C5A52A] text-white placeholder-gray-600 font-mono text-sm px-3 py-2 outline-none transition-colors resize-none" />
              </div>

              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#C5A52A] border border-[#C5A52A]/40 bg-[#C5A52A]/5 px-3 py-2">
                MOCKED in local preview · real delivery via Resend on CYQR
              </div>

              <button onClick={share} disabled={sending || recipients.length === 0}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#C5A52A] disabled:bg-[#1a1a1a] disabled:text-gray-500 hover:bg-[#D4B93A] text-black font-mono text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors"
                data-testid="qcca-mb-share-send">
                <Send className="w-3 h-3" /> {sending ? "Queuing…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
